import {Component, HostListener, Input} from '@angular/core';
import {AlertLevel} from "../../enums/alert-level";
import {transition} from "@angular/animations";
import {AlertBannerModel} from "../../models/alert-banner-model";
import {GlobalAlertService} from "../../_services/global-alert.service";
import {TailwindTransitionDuration} from "../../enums/tailwind/tailwind-transition-duration";
import {AuthenticationService} from "../../_auth/authentication.service";
import {PermissionService} from "../../_auth/permission.service";
import {AppRoutes} from "../../enums/routes";
import {AutomationRoutes} from "../../_modules/automation/x-models/x-enums/automation-routes";
import {NavbarService} from "../navbar-base/navbar.service";
import {CommonModule} from "@angular/common";


@Component({
  selector: 'app-alert-banner',
  templateUrl: './alert-banner.component.html',
  styleUrls: ['./alert-banner.component.scss'],
  imports: [
    CommonModule
  ]
})
export class AlertBannerComponent {
  @Input() AlertBannerModel: AlertBannerModel = new AlertBannerModel();
  @Input() IsHoveredOver: boolean = false;
  public tailwindTransitionDuration: string = TailwindTransitionDuration.Duration1000.toString();
  public globalGuid: string = ""
  InTransition: boolean = false;
  IsVisible: boolean = false;
  protected readonly AlertLevel = AlertLevel;
  protected readonly transition = transition;
  protected readonly location = location;
  protected readonly Routes = AppRoutes;
  protected readonly AutomationRoutes = AutomationRoutes;
  private _timeoutId: any | undefined;
  // If any value within this dictionary is true, then the alert banner will be visible.
  private IsVisibleDict: { [key: string]: boolean } = {"Default": false, "HoveredOver": false,};

  /**
   * The Tailwind class(es) that place the banner under whatever chrome the current session is wearing.
   *
   * The banner is `position: fixed`, so it is placed by **this** offset alone — the padding on the shell's
   * `<main>` does not reach it. That makes this getter the single place every shell's chrome height has to be
   * restated, and therefore the single place it can silently drift. It has drifted twice:
   *
   * - **#205** — an Operator matched {@link AuthenticationService.isPanelUser} and inherited the employee
   *   offset, landing 24 px below its own bar.
   * - **#223** — the employee offset itself was wrong (`top-10 lg:top-20`, i.e. 40/80 px) even though the
   *   employee panel wears the *same* 56 px bar. #205 branched the Operator out but left that untouched.
   *
   * Every number below therefore cites the shell it comes from, so the next person can check it rather than
   * trust it:
   *
   * - Operator — `operator.component.html`'s `h-14` bar ⇒ {@link OPERATOR_TOP_BAR_HEIGHT_PX} (56 px) ⇒ `top-14`.
   * - Employee panel — `employee-panel.component.html`'s `fixed inset-x-0 top-0 h-14` bar ⇒
   *   {@link EMPLOYEE_PANEL_TOP_BAR_HEIGHT_PX} (56 px, **at every breakpoint** — the bar has no responsive
   *   height) ⇒ `top-14`. While any gate is warning — a flagged submission (#404) or an outstanding contact
   *   link (#558) — that shell also draws a 40 px strip ({@link EMPLOYEE_PANEL_REVIEW_BANNER_HEIGHT_PX}) under
   *   the bar, so the chrome becomes 96 px ⇒ `top-24`, which is exactly what the panel's own content offset
   *   does (`showsBanner ? 'top-24' : 'top-14'`). Both read the one `isGateReviewDue` question.
   * - Admin/office — `navbar-office.component.html`'s `h-14` bar ⇒ `top-14`; while its second row is expanded
   *   that row adds `h-24` (96 px) below the bar, so the chrome is 152 px ⇒ `top-[9.5rem]`, the same literal
   *   `app.component.html` uses for the content area (`!top-[9.5rem]`).
   * - Logged out / the mailing module / a rendered automation view — no chrome at all.
   *
   * Returns a single class list rather than a truth map so the cases stay visibly exclusive; the empty string is
   * a real state (an automation *view* is chromeless and the banner rides in flow).
   */
  get TopOffsetClass(): string {
    const path = this.location.pathname;
    // Logged out, or the chromeless mailing module: flush against the top of the viewport.
    if (!this.authenticationService.userValue || path.startsWith('/' + this.Routes.MailingModule)) return '!top-0';
    // Operator first — an operator is also a panel user, so this has to win over the employee offset (#205).
    if (this.permissionService.IsOperator()) return 'top-14';
    // The employee panel: a 56 px bar, plus the 40 px gate strip while any gate is warning (#223, #558).
    // `isGateReviewDue` rather than `isFormsReviewDue` is load-bearing: the contact gate raises the SAME single
    // strip, so asking only about forms leaves this banner 40 px too high on a contact-warned session — the
    // exact drift the getter exists to stop happening a third time.
    if (this.authenticationService.isPanelUser()) {
      return this.authenticationService.isGateReviewDue ? 'top-24' : 'top-14';
    }
    // A rendered automation view carries no chrome at all — leave the banner where the flow puts it.
    if (path.startsWith('/' + this.Routes.AutomationModule + '/' + this.AutomationRoutes.Views)) return '';
    // The admin navbar: 56 px, or 152 px while its second row is expanded.
    return this.NavbarService.hasExpanded() ? 'top-[9.5rem]' : 'top-14';
  }

  constructor(private globalAlertService: GlobalAlertService, public authenticationService: AuthenticationService,
              public NavbarService: NavbarService, private permissionService: PermissionService) {
    this.globalAlertService.IsAlertBannerVisibleChanged.subscribe((value) => {
        if (value) {
          this.AlertBannerModel = this.globalAlertService.AlertBannerModel;
          this.Show();
        }
      }
    )
  }

  @HostListener('mouseenter') onMouseEnter() {
    this.Appear("HoveredOver");
  }

  @HostListener('mouseleave') onMouseLeave() {
    this.Disappear("HoveredOver");
  }

  @Input() Show(message: string = "", header: string = "", level: AlertLevel = AlertLevel.info, duration: number = 5000) {
    /**
     * Shows the alert banner with the specified parameters.
     * Calls the Appear and Disappear function.
     * If the parameter is not default, then it will override the default value in the AlertBannerModel.
     *
     * @param message Overrides the AlertBannerModel.Message
     * @param header Overrides the AlertBannerModel.Header
     * @param level Overrides the AlertBannerModel.Level
     * @param duration Overrides the AlertBannerModel.DisplayDuration
     *
     * @return void
     */
    this.OverrideProperties(message, header, level, duration);

    this.Appear("Default");

    clearTimeout(this._timeoutId);
    let localGuid = crypto.randomUUID()
    this.globalGuid = localGuid

    this._timeoutId = setTimeout(() => {
      if (this.globalGuid != localGuid) return;
      this.Disappear("Default");
    }, Math.abs(this.AlertBannerModel.DisplayDuration))

  }

  Appear(key: string = "Default") {
    /**
     * Makes the alert banner visible. Sets key string to true in IsVisibleDict.
     *
     * @param key The key string to set to true in IsVisibleDict.
     *
     * @return void
     */
    this.IsVisibleDict[key] = true;
    this.IsVisible = true;
  }

  Disappear(key: string = "Default") {
    /**
     * Sets key string to false in IsVisibleDict. Only makes the alert banner invisible if all values in IsVisibleDict are false. (OR logic)
     *
     * @param key The key string to set to false in IsVisibleDict.
     *
     * @return void
     */
    this.IsVisibleDict[key] = false;
    if (this.EvaluateIsVisible())
      return

    // Start fading effect
    this.InTransition = true;
    setTimeout(() => {
      this.IsVisible = false;
      this.InTransition = false;
    }, Number(this.tailwindTransitionDuration));

  }

  EvaluateIsVisible() {
    /**
     * Evaluates if the alert banner should be visible. Or logic.
     *
     * @return boolean
     */
    for (let key in this.IsVisibleDict) {
      if (this.IsVisibleDict[key])
        return true;
    }
    return false;
  }

  private OverrideProperties(message: string = "", header: string = "", level: AlertLevel = AlertLevel.info, duration: number = 5000) {
    /**
     * If the parameter is not default, then it will override the default value in the AlertBannerModel. Can be used to override one or more properties.
     *
     * @param message Overrides AlertBannerModel.Message
     * @param header Overrides AlertBannerModel.Header
     * @param level Overrides AlertBannerModel.Level
     * @param duration Overrides AlertBannerModel.DisplayDuration
     *
     * @return void
     */
    if (message != "") {
      this.AlertBannerModel.Message = message;
    }
    if (header != "") {
      this.AlertBannerModel.Header = header;
    }
    if (level != AlertLevel.info) {
      this.AlertBannerModel.Level = level;
    }
    if (duration != 5000) {
      this.AlertBannerModel.DisplayDuration = duration;
    }
  }
}




